import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { collectionPageSchema } from "@/lib/aeo";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LeadFormModal } from "@/components/LeadFormModal";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "เปรียบเทียบ NYC Language Institute vs DIY · ฟรีแลนซ์ · สำนักงานอื่น — แปล รับรอง Notary ที่ไหนดีที่สุด?";
const DESC =
  "ตัดสินใจง่ายขึ้น — เปรียบเทียบ NYC Legal & Notary กับการทำเอง · นักแปลฟรีแลนซ์ — ปรึกษาฟรีก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ";

export const Route = createFileRoute("/compare/")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/compare", "th")},
      { name: "description", content: ctaDesc(DESC, "/compare", "th")},
      { name: "keywords", content: "บริษัทแปลเอกสารที่ไหนดี, Notary ที่ไหนดี, NYC Legal เคสตัวอย่าง, แปลเอกสารยื่นวีซ่าที่ไหนดี" },
      { property: "og:title", content: ctaTitle(TITLE, "/compare", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/compare", "th")},
      { property: "og:url", content: `${SITE}/compare` },
    ],
    links: [{ rel: "canonical", href: `${SITE}/compare` }, ...hreflangLinks("/compare", "/en/compare")],
    scripts: [
      collectionPageSchema({ path: "/compare", name: TITLE, description: DESC }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
            { "@type": "ListItem", position: 2, name: "เปรียบเทียบบริการ", item: `${SITE}/compare` },
          ],
        }),
      },
    ],
  }),
  component: ComparePage,
});

type Cell = { v: string; tone?: "good" | "warn" | "bad" };
type Row = { feature: string; nyc: Cell; diy: Cell; freelance: Cell; agency: Cell };

const ROWS: Row[] = [
  {
    feature: "ขึ้นทะเบียนกรมการกงสุล",
    nyc: { v: "✅ จดทะเบียน 0435567000061", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "❌ ส่วนใหญ่ไม่ได้ขึ้นทะเบียน", tone: "bad" },
    agency: { v: "⚠️ บางแห่งเท่านั้น", tone: "warn" },
  },
  {
    feature: "Notary Public ในทีม",
    nyc: { v: "✅ 6 ทนายในสภาทนายความ", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "❌ ต้องไปหาทนายเอง", tone: "bad" },
    agency: { v: "⚠️ 1–2 คน หรือ outsource", tone: "warn" },
  },
  {
    feature: "นักแปล NAATI Australia",
    nyc: { v: "✅ มีในทีม + เครือข่าย NAATI", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "⚠️ หายาก ต้องเสิร์ชเอง", tone: "warn" },
    agency: { v: "❌ ส่วนใหญ่ไม่มี", tone: "bad" },
  },
  {
    feature: "บริการนำไปรับรองที่ MFA / สถานทูต",
    nyc: { v: "✅ รับ-ส่งครบจบที่เดียว", tone: "good" },
    diy: { v: "❌ ต้องคิว 1–3 วัน ลางาน", tone: "bad" },
    freelance: { v: "❌ ลูกค้าทำเอง", tone: "bad" },
    agency: { v: "⚠️ บางที่คิดค่าบริการแพง", tone: "warn" },
  },
  {
    feature: "จำนวนภาษาที่รองรับ",
    nyc: { v: "✅ 50+ ภาษา", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "⚠️ 1–2 ภาษาต่อคน", tone: "warn" },
    agency: { v: "⚠️ 5–15 ภาษา", tone: "warn" },
  },
  {
    feature: "ราคาเริ่มต้น (ไทย-อังกฤษ/หน้า)",
    nyc: { v: "฿500", tone: "good" },
    diy: { v: "ค่าเสียโอกาส + เสี่ยงถูกปฏิเสธ", tone: "bad" },
    freelance: { v: "฿200–฿400 (ไม่รับรอง)", tone: "warn" },
    agency: { v: "฿600–฿1,500", tone: "warn" },
  },
  {
    feature: "ความเร็วงานเร่งด่วน",
    nyc: { v: "✅ Rush 4 ชม.", tone: "good" },
    diy: { v: "❌ ขึ้นกับคิวราชการ", tone: "bad" },
    freelance: { v: "⚠️ 1–3 วัน", tone: "warn" },
    agency: { v: "⚠️ 1–5 วัน", tone: "warn" },
  },
  {
    feature: "ตอบกลับใบเสนอราคา",
    nyc: { v: "✅ 15 นาที", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "⚠️ หลายชั่วโมง", tone: "warn" },
    agency: { v: "⚠️ 1 วันทำการ", tone: "warn" },
  },
  {
    feature: "พื้นที่ครอบคลุม",
    nyc: { v: "✅ 50 เขต กทม. + 77 จังหวัด", tone: "good" },
    diy: { v: "เฉพาะที่ตัวเองเข้าถึง", tone: "warn" },
    freelance: { v: "⚠️ ออนไลน์", tone: "warn" },
    agency: { v: "⚠️ 1–3 สาขา", tone: "warn" },
  },
  {
    feature: "ออกใบกำกับภาษีเต็มรูป + WHT 3%",
    nyc: { v: "✅ ออกได้ทันที", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "❌ ส่วนใหญ่ไม่มี", tone: "bad" },
    agency: { v: "✅ ออกได้", tone: "good" },
  },
  {
    feature: "รับประกัน — ถูกปฏิเสธ คืนเงิน + แก้ฟรี",
    nyc: { v: "✅ รับประกัน 100%", tone: "good" },
    diy: { v: "❌ รับความเสี่ยงเอง", tone: "bad" },
    freelance: { v: "❌ ไม่มีรับประกัน", tone: "bad" },
    agency: { v: "⚠️ บางแห่ง", tone: "warn" },
  },
  {
    feature: "การรับรองมาตรฐานสากล",
    nyc: { v: "✅ ISO 17100 · ISO 9001 · NAATI · สภาทนายความ", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "❌", tone: "bad" },
    agency: { v: "⚠️ บางส่วน", tone: "warn" },
  },
  {
    feature: "ทีม Account Manager 1-on-1",
    nyc: { v: "✅ มีให้ลูกค้าองค์กร", tone: "good" },
    diy: { v: "—", tone: "bad" },
    freelance: { v: "❌", tone: "bad" },
    agency: { v: "⚠️ เฉพาะลูกค้า VIP", tone: "warn" },
  },
];

const toneClass = (t?: Cell["tone"]) =>
  t === "good"
    ? "bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
    : t === "warn"
    ? "bg-amber-50 text-amber-900 ring-1 ring-amber-200"
    : t === "bad"
    ? "bg-rose-50 text-rose-900 ring-1 ring-rose-200"
    : "bg-white text-navy-deep ring-1 ring-black/10";

function ComparePage() {
  return (
    <main className="min-h-screen bg-gradient-to-b from-[#fafaf7] to-white">
      {/* Hero */}
      <section className="border-b border-amber-500/20 bg-navy-deep text-white">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <nav className="mb-4 text-xs text-white/80" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber-300">หน้าแรก</Link>
            <span className="mx-2">›</span>
            <span className="text-amber-300">เปรียบเทียบบริการ</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            ⚖️ Side-by-side Comparison
          </div>
          <h1 className="mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            แปล · รับรอง · Notary<br />
            <span className="text-amber-400">ที่ไหนดีที่สุดสำหรับคุณ?</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-white/80 sm:text-lg">
            ก่อนตัดสินใจ — ลองเทียบ <strong className="text-white">NYC Legal & Notary</strong> กับ
            การทำเอง (DIY) · นักแปลฟรีแลนซ์ · และสำนักงานทั่วไป ครบทุกมิติที่สำคัญ
          </p>
          <div className="quick-answer mt-6 max-w-3xl rounded-2xl border border-amber-400/40 bg-amber-500/10 p-5" data-speakable="true" itemScope itemType="https://schema.org/Question">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-amber-300">AI Quick Answer</div>
            <h2 className="text-base font-bold text-white sm:text-lg" itemProp="name">แปลและรับรองเอกสารที่ไหนดีที่สุดในไทย?</h2>
            <div itemProp="acceptedAnswer" itemScope itemType="https://schema.org/Answer">
              <p className="direct-answer mt-2 text-sm leading-relaxed text-white/85" itemProp="text">NYC Language Institute เป็นทางเลือกที่ครอบคลุมที่สุด — มี Notary Public 6 ท่าน, NAATI certified, รองรับ 50+ สถานทูตและกรมการกงสุล MFA จบในที่เดียว ราคาเริ่ม ฿500/หน้า เทียบกับนักแปลฟรีแลนซ์ที่ราคาถูกกว่าแต่ไม่มีรับรองสถานทูต และสำนักงานทั่วไปที่ต้องส่งต่อ vendor หลายแห่ง</p>
            </div>
          </div>
        </div>
      </section>

      {/* Big comparison table */}
      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
        <div className="overflow-x-auto rounded-2xl border border-black/10 bg-white shadow-sm">
          <table className="w-full min-w-[800px] border-collapse text-sm">
            <thead>
              <tr className="bg-navy-deep text-white">
                <th className="sticky left-0 z-10 border-b border-amber-500/30 bg-navy-deep p-4 text-left text-xs font-bold uppercase tracking-wider">
                  ฟีเจอร์ / บริการ
                </th>
                <th className="border-b border-amber-500/30 bg-amber-500 p-4 text-center text-xs font-extrabold text-navy-deep">
                  <div className="text-sm">NYC Legal & Notary</div>
                  <div className="text-[10px] font-bold uppercase tracking-wider opacity-70">⭐ แนะนำ</div>
                </th>
                <th className="border-b border-amber-500/30 p-4 text-center text-xs font-bold">ทำเอง (DIY)</th>
                <th className="border-b border-amber-500/30 p-4 text-center text-xs font-bold">ฟรีแลนซ์</th>
                <th className="border-b border-amber-500/30 p-4 text-center text-xs font-bold">สำนักงานทั่วไป</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.feature} className={i % 2 ? "bg-amber-50/30" : "bg-white"}>
                  <th scope="row" className="sticky left-0 border-b border-black/5 bg-inherit p-4 text-left text-xs font-bold text-navy-deep">
                    {r.feature}
                  </th>
                  <td className="border-b border-black/5 p-3 text-center">
                    <span className={`inline-block rounded-lg px-2.5 py-1.5 text-[11px] font-bold ${toneClass(r.nyc.tone)}`}>
                      {r.nyc.v}
                    </span>
                  </td>
                  <td className="border-b border-black/5 p-3 text-center">
                    <span className={`inline-block rounded-lg px-2.5 py-1.5 text-[11px] ${toneClass(r.diy.tone)}`}>{r.diy.v}</span>
                  </td>
                  <td className="border-b border-black/5 p-3 text-center">
                    <span className={`inline-block rounded-lg px-2.5 py-1.5 text-[11px] ${toneClass(r.freelance.tone)}`}>{r.freelance.v}</span>
                  </td>
                  <td className="border-b border-black/5 p-3 text-center">
                    <span className={`inline-block rounded-lg px-2.5 py-1.5 text-[11px] ${toneClass(r.agency.tone)}`}>{r.agency.v}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Decision helper */}
      <section className="border-y border-amber-500/20 bg-amber-50/40">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6">
          <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">เลือกแบบไหนดีสำหรับสถานการณ์ของคุณ?</h2>
          <div className="mt-6 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                t: "🇹🇭 ยื่นวีซ่า / สถานทูต / MFA",
                rec: "NYC Legal",
                why: "ต้องการตราประทับที่ราชการ/สถานทูตรับ — ฟรีแลนซ์มักถูกปฏิเสธ",
                tone: "good",
              },
              {
                t: "📝 เอกสารส่วนตัว ใช้ในประเทศเฉยๆ",
                rec: "ฟรีแลนซ์ ก็พอ",
                why: "ถ้าไม่ต้องรับรอง MFA/สถานทูต ใช้ฟรีแลนซ์ประหยัดเงินได้",
                tone: "warn",
              },
              {
                t: "⚡ Rush ต้องได้ภายในวันนี้",
                rec: "NYC Legal",
                why: "Rush 4 ชม. + รับ-ส่งครบจบ ไม่มีเจ้าอื่นทำได้",
                tone: "good",
              },
              {
                t: "🏢 องค์กร / สัญญารายปี",
                rec: "NYC Legal",
                why: "Account Manager + ส่วนลด Wholesale + WHT 3% + ISO certified",
                tone: "good",
              },
            ].map((c) => (
              <div
                key={c.t}
                className={`rounded-2xl bg-white p-5 ring-1 ${c.tone === "good" ? "ring-emerald-300" : "ring-amber-300"}`}
              >
                <div className="text-sm font-extrabold text-navy-deep">{c.t}</div>
                <div className={`mt-2 inline-block rounded-full px-2.5 py-1 text-[11px] font-bold ${c.tone === "good" ? "bg-emerald-100 text-emerald-900" : "bg-amber-100 text-amber-900"}`}>
                  → {c.rec}
                </div>
                <div className="mt-2 text-xs leading-relaxed text-navy-deep/70">{c.why}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Real costs of DIY / Freelance */}
      <section className="mx-auto max-w-5xl px-4 py-14 sm:px-6">
        <h2 className="text-2xl font-extrabold text-navy-deep sm:text-3xl">ต้นทุนแฝง — ที่หลายคนไม่ได้คิดถึง</h2>
        <p className="mt-2 text-sm text-navy-deep/70">ราคาถูกกว่าจริงๆ หรือไม่? ดูตัวอย่างเคสจริง</p>
        <div className="mt-6 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-rose-200 bg-rose-50/40 p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-rose-700">เคส: DIY / ฟรีแลนซ์</div>
            <h3 className="mt-1 text-lg font-extrabold text-navy-deep">ลูกค้าแปลเอกสารยื่นวีซ่าเชงเก้นเอง</h3>
            <ul className="mt-3 space-y-2 text-sm text-navy-deep/80">
              <li>• ค่าแปลฟรีแลนซ์ ฿200 × 5 หน้า = ฿1,000</li>
              <li>• สถานทูตปฏิเสธ — ต้องแปลใหม่ที่บริษัทขึ้นทะเบียน</li>
              <li>• เลื่อนคิว 2 สัปดาห์ → ตั๋วเครื่องบินต้องซื้อใหม่ +฿15,000</li>
              <li>• ลางาน 2 วันเพิ่ม = ค่าเสียโอกาส ฿4,000</li>
              <li className="border-t border-rose-300 pt-2 font-bold text-rose-800">💸 รวมเสีย ~฿20,000+</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">เคส: NYC Legal</div>
            <h3 className="mt-1 text-lg font-extrabold text-navy-deep">ลูกค้าให้ NYC ดูแลครบจบ</h3>
            <ul className="mt-3 space-y-2 text-sm text-navy-deep/80">
              <li>• ค่าแปล 5 หน้า ฿500 × 5 = ฿2,500</li>
              <li>• ค่ารับรองบริษัท ฟรี (เมื่อแปลกับเรา)</li>
              <li>• ค่านำไปรับรอง MFA + สถานทูต = ฿5,000</li>
              <li>• เอกสารผ่านครั้งเดียว · ไม่ต้องลางาน</li>
              <li className="border-t border-emerald-300 pt-2 font-bold text-emerald-800">✅ รวม ฿7,500 · จบใน 3 วัน</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6">
        <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-navy-deep to-[#0a1a3d] p-10 text-center text-white shadow-xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">ตัดสินใจได้แล้ว? ให้เราดูแลให้ครบจบ</h2>
          <p className="mt-3 text-sm text-white/75 sm:text-base">
            ส่งเอกสารให้ทีมประเมินฟรี — ตอบใน 15 นาที · ราคาโปร่งใส · รับประกันคืนเงิน
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button type="button" className="rounded-full bg-amber-500 px-7 py-3.5 text-sm font-extrabold text-navy-deep transition hover:bg-amber-400">
                  ✍️ ขอใบเสนอราคาฟรี
                </button>
              }
            />
            <Link to="/pricing" className="rounded-full border border-white/30 bg-white/5 px-7 py-3.5 text-sm font-bold text-white transition hover:bg-white/10">
              ดูตารางราคา →
            </Link>
            <a href="tel:+66832494999" className="rounded-full border border-amber-400/40 bg-amber-500/10 px-7 py-3.5 text-sm font-bold text-amber-300 transition hover:bg-amber-500/20">
              📞 083-249-4999
            </a>
          </div>
        <RelatedHubGrid locale="th" />
      </div>
      </section>
    </main>
  );
}
